import { StoreState } from '../types';
import { Epic, combineEpics, ActionsObservable, StateObservable, ofType } from 'redux-observable';
import { Observable, merge, empty } from 'rxjs';
import { LOCATION_CHANGE, LocationChangeAction } from 'connected-react-router';
import { distinctUntilChanged, switchMap, map, takeUntil, catchError } from 'rxjs/operators';
import { observeChanges, observeChangesQuery, observeChangesDoc } from '../firebase';
import { schoolUserUpdated, SchoolUserUpdated } from '../actions/SchoolUserActions';
import { schoolSessionUpdated, SchoolSessionUpdated } from '../actions/SchoolSessionActions';
import { schoolSpotUpdated, SchoolSpotUpdated } from '../actions/SchoolSpotActions';
import { schoolSettingsUpdated, SchoolSettingsUpdated } from '../actions/SchoolSettingsActions';
import { schoolUpdated, SchoolUpdated, schoolBookingUpdated } from '../actions/SchoolBookingActions';
import { UserSignedIn, UserSignedOut, SIGN_OUT, SignOut, USER_SIGNED_IN } from '../actions/AuthActions';
import { schoolInstructorExperienceUpdated } from '../actions/SchoolJobsActions';

type SchoolSubscriptionAction =
    | SignOut
    | UserSignedIn
    | UserSignedOut
    | LocationChangeAction
    | SchoolUserUpdated
    | SchoolSessionUpdated
    | SchoolSpotUpdated
    | SchoolSettingsUpdated
    | SchoolUpdated;

const getSchoolId = (pathname: string) => {
    const match = RegExp(`/manage/school/([^/]+)`).exec(pathname);
    return match ? match[1] : null;
};

const getBookingsSince = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return (startOfMonth.getDate() > oneWeekAgo.getDate() ? oneWeekAgo : startOfMonth).toISOString().split('T')[0];
};

const schoolSubActions = (schoolId: string) =>
    merge(
        observeChanges(`schools/${schoolId}/users`).pipe(map(schoolUserUpdated)),
        observeChanges(`schools/${schoolId}/sessions`).pipe(map(schoolSessionUpdated)),
        observeChanges(`schools/${schoolId}/spots`).pipe(map(schoolSpotUpdated)),
        observeChanges(`schools/${schoolId}/settings`).pipe(map(schoolSettingsUpdated)),
        observeChanges(`schools/${schoolId}/instructorExperience`).pipe(map(schoolInstructorExperienceUpdated)),
        observeChangesQuery(`schools/${schoolId}/bookings`, 'end', '>=', getBookingsSince()).pipe(map(schoolBookingUpdated)),
        observeChangesDoc(`schools/${schoolId}`).pipe(map(schoolUpdated))
    ) as Observable<SchoolSubscriptionAction>;

const schoolSubscriptionsEpic: Epic<SchoolSubscriptionAction, SchoolSubscriptionAction, StoreState> =
    (action$: ActionsObservable<SchoolSubscriptionAction>, state$: StateObservable<StoreState>): Observable<SchoolSubscriptionAction> =>
        action$.pipe(
            ofType(LOCATION_CHANGE, USER_SIGNED_IN),
            map(() => state$.value.router.location.pathname),
            distinctUntilChanged((x, y) => x === y, getSchoolId),
            switchMap(l => {
                const schoolId = getSchoolId(l);
                const signedIn = !!state$.value.auth.user;
                return signedIn && schoolId
                    ? schoolSubActions(schoolId).pipe(takeUntil(action$.ofType(SIGN_OUT)))
                    : empty();
            }),
            catchError((err, caught) => {
                console.error('schoolSubscriptionsEpic', err);
                return caught;
            })
        );

export default combineEpics<SchoolSubscriptionAction, SchoolSubscriptionAction, StoreState>(schoolSubscriptionsEpic);
